import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useContentOperations } from "@/hooks/useContentOperations";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import CollectionHeader from "./CollectionHeader";
import { ContentItem } from "./content-card/types";
import RefreshButton from "./collection-grid/RefreshButton";
import CollectionContent from "./collection-grid/CollectionContent";
import { useRefreshThumbnails } from "@/hooks/useRefreshThumbnails";
import EditContentDialog from "./EditContentDialog";
import { usePersistentViewMode } from "@/hooks/usePersistentViewMode";
import { useCollections } from "@/contexts/collections";
import { fetchContentWithTags } from "@/hooks/useContentTags";
import { cn } from "@/lib/utils";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";

const DashboardContent = () => {
  const { user } = useAuth();
  const { handleContentClick, handleDelete, handleArchive } = useContentOperations();
  const { viewMode, setViewMode } = usePersistentViewMode();
  const { refreshCollections, collections } = useCollections();
  
  // State for edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editContentId, setEditContentId] = useState<string>('');
  const [editContentTitle, setEditContentTitle] = useState<string>('');

  // State for tag filtering
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagLogicType, setTagLogicType] = useState<"AND" | "OR">("AND");

  // Force refresh collections when Dashboard is mounted
  useEffect(() => {
    console.log("DashboardContent mounted, refreshing collections");
    
    // Track dashboard page view - using 'inbox' as the closest matching navigation type
    Tier1Tracking.trackNavigation('inbox', undefined, 'All Content', undefined, user);
    
    refreshCollections();
    
    // Also refresh on tab focus to ensure consistent collection order
    const handleFocus = () => {
      console.log("Window focused, refreshing collections in DashboardContent");
      refreshCollections();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshCollections, user]);

  // Listen for changes in selected tags (from TagFilterControls)
  useEffect(() => {
    const handleCustomEvent = (e: Event) => {
      if (e instanceof CustomEvent && e.detail) {
        try {
          const data = e.detail as { tagIds: string[], logicType: "AND" | "OR" };
          console.log("Dashboard received tag selection change event:", data);
          setSelectedTagIds(Array.isArray(data.tagIds) ? data.tagIds : []);
          setTagLogicType(data.logicType || "AND");
          
          // Track tag filtering usage
          if (data.tagIds && data.tagIds.length > 0) {
            Tier1Tracking.trackEvent('tag_filter_applied', 'discovery', {
              tag_count: data.tagIds.length,
              logic_type: data.logicType
            }, user);
          }
        } catch (error) {
          console.error("Error handling custom event:", error);
        }
      }
    };
    
    window.addEventListener('selectedTagsChanged', handleCustomEvent as EventListener);
    
    return () => {
      window.removeEventListener('selectedTagsChanged', handleCustomEvent as EventListener);
    };
  }, [user]);

  // Load selected tags from localStorage on initial load
  useEffect(() => {
    const storedTags = localStorage.getItem('selectedTags');
    if (storedTags) {
      try {
        const parsedData = JSON.parse(storedTags) as { tagIds: string[], logicType: "AND" | "OR" };
        
        // Ensure we have valid arrays and default values if needed
        setSelectedTagIds(Array.isArray(parsedData.tagIds) ? parsedData.tagIds : []);
        setTagLogicType(parsedData.logicType || "AND");
        
        console.log("Loaded selected tags from storage:", parsedData);
      } catch (error) {
        console.error("Error parsing stored tags:", error);
        // Reset to defaults if there's an error
        setSelectedTagIds([]);
        setTagLogicType("AND");
      }
    }
  }, []);

  // Get hidden collection IDs
  const hiddenCollectionIds = collections
    .filter(collection => collection.hidden)
    .map(collection => collection.id);

  const hasTagFilter = selectedTagIds.length > 0;

  // Modified to not depend on collections.length to fetch content
  const { data: contentItems = [], isLoading, error, refetch } = useQuery({
    queryKey: ["contentItems", user?.id, hiddenCollectionIds, selectedTagIds, tagLogicType],
    queryFn: async () => {
      if (!user) {
        console.log("User not logged in");
        return [];
      }

      try {
        console.log("Fetching content with tag filters:", {
          selectedTagIds,
          tagLogicType,
          hasTagFilter,
          collectionsLoaded: collections.length
        });

        // First get the base content items
        const { data: contentData, error: contentError } = await supabase
          .from("content_items")
          .select("*")
          .eq("user_id", user.id)
          .eq("archived", false)
          .order("created_at", { ascending: false });

        if (contentError) {
          console.error("Error fetching content:", contentError);
          throw contentError;
        }

        // Get collection associations
        const { data: contentCollections, error: collectionsError } = await supabase
          .from("content_collections")
          .select("content_id, collection_id");

        if (collectionsError) {
          console.error("Error fetching content collections:", collectionsError);
          return contentData as ContentItem[];
        }

        // Apply tag filtering if needed
        let filteredItems: any[] = contentData;
        
        if (hasTagFilter) {
          console.log(`Filtering by ${selectedTagIds.length} tags with ${tagLogicType} logic`);
          
          // Get content that matches the tag filter
          const tagFilteredItems = await fetchContentWithTags(user.id, selectedTagIds, tagLogicType);
          console.log(`Tag filtered items count: ${tagFilteredItems.length}`);
          
          // Get the IDs of tag-filtered content
          const tagFilteredIds = new Set(tagFilteredItems.map(item => item.id));
          
          // Filter the base items to only include those that match the tag filter
          filteredItems = contentData.filter(item => tagFilteredIds.has(item.id));
          console.log(`Final items after tag filtering: ${filteredItems.length}`);
        }

        // Enhanced logging for collection colors debugging
        console.log("Collections loaded:", collections.length); 
        console.log("Collection colors available:", collections.map(c => `${c.id}: ${c.color}`));

        // Create a direct map of collection colors for faster lookup
        const collectionColorMap: Record<string, string> = {};
        collections.forEach(collection => {
          if (collection.id && collection.color) {
            collectionColorMap[collection.id] = collection.color;
          }
        });

        // Filter out content items that are in hidden collections and apply collection info
        return filteredItems
          .map(item => {
            const association = contentCollections?.find(cc => cc.content_id === item.id);
            
            if (!association) {
              return { 
                ...item, 
                collection_id: null,
                collection_color: "#6B7789" // Default color for inbox
              }; 
            }

            // First try to get the color from our direct map (fastest)
            const collectionColor = collectionColorMap[association.collection_id];
            
            // If not found in our map, try to find the collection in the collections array
            if (collectionColor) {
              console.log(`Item ${item.id}: Found color ${collectionColor} for collection ${association.collection_id}`);
              return { 
                ...item, 
                collection_id: association.collection_id,
                collection_color: collectionColor
              };
            }
            
            // Fallback to finding the collection in the array
            const collection = collections?.find(c => c.id === association.collection_id);
            console.log(`Item ${item.id}: Using color ${collection?.color || "#6B7789"} for collection ${association.collection_id}`);
            
            return { 
              ...item, 
              collection_id: association.collection_id,
              collection_color: collection?.color || "#6B7789"
            };
          })
          .filter(item => {
            // If the item is not in any collection, keep it (it's in Inbox)
            if (!item.collection_id) return true;
            
            // Filter out items that are in hidden collections
            return !hiddenCollectionIds.includes(item.collection_id);
          });
      } catch (e) {
        console.error("Error in content query:", e);
        return [];
      }
    },
    // Important change: Only check if user is logged in, don't require collections
    enabled: !!user,
    staleTime: 0, // Don't cache this data to ensure we always get fresh colors
  });

  const { isRefreshing, handleRefreshThumbnails } = useRefreshThumbnails(refetch);

  const handleEditContent = (contentId: string, currentTitle: string) => {
    console.log('[DashboardContent] Opening edit dialog for content:', contentId, currentTitle);
    
    // Track content edit initiation
    Tier1Tracking.trackEvent('content_edit_initiated', 'curation', {
      content_id: contentId
    }, user);
    
    setEditContentId(contentId);
    setEditContentTitle(currentTitle || 'Untitled Content');
    setEditDialogOpen(true);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Fixed header with sticky positioning to keep it visible */}
      <div className="flex-none sticky top-0 z-20">
        <CollectionHeader
          title="All Your Content"
          description="saved in one place"
          viewMode={viewMode}
          onViewModeChange={(newMode) => {
            setViewMode(newMode);
            // Track view mode change
            Tier1Tracking.trackEvent('view_mode_changed', 'discovery', {
              old_mode: viewMode,
              new_mode: newMode
            }, user);
          }}
        />
      </div>
      
      {/* Scrollable content below the fixed header */}
      <div className={cn(
        "flex-1 overflow-auto",
        // Only add padding for Frame, Grid, and List views
        viewMode !== 'network' && viewMode !== 'freeform' ? 'px-7' : ''
      )}>
        <CollectionContent
          contentItems={contentItems || []}
          isLoading={isLoading}
          viewMode={viewMode}
          onContentClick={handleContentClick}
          onDeleteContent={handleDelete}
          onEditContent={handleEditContent}
          onArchiveContent={handleArchive}
        />
      </div>

      {/* Edit Content Dialog */}
      <EditContentDialog 
        contentId={editContentId}
        title={editContentTitle}
        isOpen={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />
    </div>
  );
};

export default DashboardContent;
